import React, { useRef, useMemo, useState } from "react";
import { useDimensions } from "common/hooks";
import styled from "styled-components";
import * as d3 from "d3";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const WrapperChart = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  svg {
    width: 100%;
    height: 100%;

    .grid {
      line {
        opacity: 0.3;
        stroke: var(--chart-axis-text-color);
      }
      text {
        font-size: 11px !important;
        font-family: var(--chart-font-family, Arial, sans-serif);
        fill: var(--chart-axis-text-color);
      }
    }

    .xAxis {
      text {
        font-size: 11px !important;
        fill: var(--chart-axis-text-color);
      }
    }

    .dumbbells {
      text {
        fill: var(--chart-axis-text-color);
        color: var(--chart-axis-text-color);
      }
      .clickBar {
        fill: transparent;
      }
    }
  }
`;

const MARGIN = { top: 30, right: 30, bottom: 30, left: 80 };

// What is left between the label of the axis and the bottom of the chart.
const LABEL_GAP = 3;

function DumbBellChart({ data, height, margin = MARGIN, xAxisLabel }) {
  const chartRef = useRef(null);
  const dimensions = useDimensions(chartRef);
  const { width } = dimensions;
  const heightChart = height - margin.top - margin.bottom;
  const widthChart = width - margin.left - margin.right;
  const [hoveredCell, setHoveredCell] = useState(null);

  const { min1, max1, min2, max2 } = useMemo(() => {
    const [m1, x1] = d3.extent(data, (d) => d.value1);
    const [m2, x2] = d3.extent(data, (d) => d.value2);
    return { min1: m1, max1: x1, min2: m2, max2: x2 };
  }, [data]);

  const yScale = useMemo(() => {
    return d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, heightChart]);
  }, [data, height]);

  const xScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([Math.min(min1, min2)-1|| 0, Math.max(max1, max2) + 1 || 10])
      .range([0, widthChart]);
  }, [data, width]);

  const allShapes = useMemo(() => data.map((d, i) => {
    if (!d.name) {
      return null;
    }

    const y = yScale(d.name) + yScale.bandwidth() / 2;

    const rightValue = Math.max(d.value1, d.value2);
    const diffLabel = `${(d.accel*100).toFixed(1)}%` === '0.0%' ? 'n/s' : `${(d.accel*100).toFixed(1)}%`;

    return (
      <g key={i}>
        <line
          x1={xScale(d.value1)}
          y1={y}
          y2={y}
          x2={xScale(d.value2)}
          opacity={0.7}
          stroke={"var(--color-grey400)"}
          strokeWidth={1}
        />
        <circle
          cy={y}
          cx={xScale(d.value1)}
          stroke="rgb(113, 117, 121)"
          fill="rgb(113, 117, 121)"
          strokeWidth={1}
          r={5}
        />
        <circle
          cy={y}
          cx={xScale(d.value2)}
          stroke="#DC143C"
          fill="#DC143C"
          strokeWidth={1}
          r={5}
        />
        <text
          x={-10}
          y={y}
          textAnchor="end"
          dominantBaseline="central"
          fontSize={12}
        >
          {d.name}
        </text>
        <text
          x={xScale(rightValue) + 10}
          y={y}
          textAnchor="start"
          dominantBaseline="central"
          fontSize={11}
        >
          {diffLabel}
        </text>
        <rect
          className="clickBar"
          x={0}
          y={y - yScale.bandwidth() / 2}
          height={yScale.bandwidth()}
          width={widthChart}
          onMouseEnter={() => {
            setHoveredCell({
              name: d.name,
              value1: d.value1,
              value2: d.value2,
              xPos: margin.left,
              yPos: y,
            });
          }}
          onMouseLeave={() => {
            setHoveredCell(null);
          }}
        />
      </g>
    );
  }), [data, xScale, yScale, widthChart, margin.left]);

  // The label of the axis goes under the values of it, on the last line the
  // margin at the bottom holds.
  const labelY = heightChart + margin.bottom - LABEL_GAP;

  const grid = useMemo(() => xScale.ticks(5).map((value, i) => (
      <g key={i}>
        <line x1={xScale(value)} x2={xScale(value)} y1={0} y2={heightChart} />
        <text
          x={xScale(value)}
          y={heightChart + 10}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {value}
        </text>
      </g>
  )), [xScale, heightChart]);

  return (
    <Wrapper>
      <WrapperChart ref={chartRef} style={{ height }}>
        <svg width={width} height={height}>
          <g
            transform={`translate(${[margin.left, margin.top].join(",")})`}
          >
            <g className="grid">{grid}</g>
            <g className="dumbbells">{allShapes}</g>
            <g className="xAxis">
              {xAxisLabel === undefined ? null : (
                <text x={widthChart / 2} y={height - 10} textAnchor="middle">
                  {xAxisLabel}
                </text>
              )}
            </g>
          </g>
        </svg>
      </WrapperChart>
      <Tooltip
        interactionData={hoveredCell}
        width={width}
        height={height}
        unit={"Mbps"}
      />
    </Wrapper>
  );
}

export default React.memo(DumbBellChart);

const TooltipWrapper = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  padding: 6px;
  transform: translate(-50%, -50%);
  font-family: "AllianceNo2-Regular" !important;

  .tooltip-extra {
    font-family: "AllianceNo2-SemiBold" !important;
  }
`;

const TooltipHeader = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
  font-size: 13px;
  font-weight: 100 !important;
`;

const TooltipContent = styled.div`
  margin: 4px 0;
  display: flex;
  align-items: center;
`;

const TooltipKey = styled.div`
  margin: 0 10px;
  width: 100px;
`;

const TooltipValue = styled.div`
  width: 80px;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const TooltipColor = styled.div`
  background-color: ${(props) => props.bgColor};
  width: 12px;
  height: 12px;
  border: 1px solid #999;
`;

export const Tooltip = ({ interactionData, width, height, unit }) => {
  if (!interactionData || Object.keys(interactionData).length <= 1) {
    return null;
  }

  const { name, value1, value2, xPos, yPos } = interactionData;

  return (
    <div
      style={{
        height,
        width: width,
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
      }}
    >
      <TooltipWrapper
        style={{
          position: "absolute",
          width: "auto",
          left: xPos,
          top: yPos - 40,
        }}
      >
        <TooltipHeader>{name}</TooltipHeader>
        <TooltipContent>
          <TooltipColor
            style={{ borderRadius: "50%" }}
            bgColor="#DC143C"
          ></TooltipColor>
          <TooltipKey>With TCPO:</TooltipKey>
          <TooltipValue>
            {value2} {unit}
          </TooltipValue>
        </TooltipContent>
        <TooltipContent>
          <TooltipColor
            style={{ borderRadius: "50%" }}
            bgColor="rgb(113, 117, 121)"
          ></TooltipColor>
          <TooltipKey>Without TCPO:</TooltipKey>
          <TooltipValue>
            {value1} {unit}
          </TooltipValue>
        </TooltipContent>
      </TooltipWrapper>
    </div>
  );
};
